import { captureException } from '@sentry/react';
import { connect as formikConnect } from 'formik';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose, setPropTypes, withHandlers } from 'recompact';

import { UNIFIED } from 'src/app/analytics/analyticsEvents';
import { withClient } from 'src/app/hocs';
import { segmentAnalyticsEvent } from 'src/app/state/actions';
import { getFacility } from 'src/app/state/selectors';
import type { Dispatch, RootState } from 'src/app/state/store';

import { ApiAutoComplete } from '../container';

const FIFTY_KM = 50000;

const mapStateToProps = (state: RootState) => {
  const facility = getFacility(state);
  return { facility };
};

const mapDispatchToProps = (dispatch: Dispatch) => ({
  trackSelectAutoCompleteAddress: () => dispatch(segmentAnalyticsEvent(UNIFIED.selectAutocompleteAddress.name)),
});

export const AddressAutoComplete = compose(
  formikConnect,
  withClient,
  setPropTypes({ relatedFields: PropTypes.shape({ zipCode: PropTypes.string.isRequired }) }),
  connect(mapStateToProps, mapDispatchToProps),
  withHandlers({
    apiCall:
      ({ client, facility }) =>
      async (searchQuery: string) => {
        try {
          const lat = facility.address.coordinates?.latitude;
          const lng = facility.address.coordinates?.longitude;

          const query = {
            input: searchQuery,
            location: `${lat} ${lng}`,
            radius: FIFTY_KM,
            components: `country:us`,
          };

          const response = await client.placeAutoComplete(query);
          const predictions = response?.data?.predictions ?? [];
          return predictions;
        } catch (error) {
          captureException(error);
          return [];
        }
      },
    selectedItemToString: () => item => {
      const street = item?.structured_formatting?.main_text;

      if (!street) {
        captureException('Google Places Autocomplete Not Returning Expected Results');
        return 'Something went wrong...';
      }

      return street;
    },
    optionItemToString: () => item => {
      if (!item?.description) {
        captureException('Google Places Autocomplete Not Returning Expected Results');
        return '';
      }

      return item.description.replace(/, USA$/, '');
    },
    setRelatedFields:
      ({ client, formik, relatedFields, trackSelectAutoCompleteAddress }) =>
      async selectedItem => {
        const { setFieldValue, setStatus } = formik;
        const placeId = selectedItem?.place_id ?? '';

        if (placeId) {
          const response = await client.googlePlaces.details(placeId);
          const details = response?.data?.data?.details;
          const zipCodeValue = details?.zip_code ?? '';

          if (relatedFields?.zipCode && zipCodeValue) {
            trackSelectAutoCompleteAddress();
            setFieldValue(relatedFields.zipCode, zipCodeValue);

            // This logic is copy-pasted from the `checkZipCode` in the ZipCode component
            if (zipCodeValue.length === 5) {
              setStatus({ submitting: true });
              try {
                const resp = await client.checkZipCodeEligibility(zipCodeValue);
                const isDeliverable = resp.data?.isDeliverable;
                setStatus({ submitting: false, isZipCodeDeliverable: isDeliverable });
                setFieldValue(`${name}Deliverable`, isDeliverable);
              } catch (e) {
                setFieldValue(`${name}Deliverable`, undefined);
                setStatus({ submitting: false, isZipCodeDeliverable: null });
              }
            } else {
              setStatus({ submitting: false, isZipCodeDeliverable: null });
              setFieldValue(`${name}Deliverable`, undefined);
            }
          }
        }
      },
  })
)(ApiAutoComplete);
